import { useState } from "react";
import { toast } from "react-toastify";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Stack, Typography,
} from "@mui/material";

import api from "../api";

const CATEGORIES = ["", "text", "image", "classifier", "embedding", "speech", "vision", "other"];

// Register a new model OR edit an existing one. `model=null` → register (paste a
// HuggingFace URL/id); otherwise edit (name fixed; repo id editable for custom only).
export default function ModelRegistryModal({ model, onClose }) {
  const editing = !!model;
  const custom = !editing || model.source === "custom";
  const [f, setF] = useState({
    model_id: model?.model_id || "",
    name: model?.name || "",
    description: model?.description || "",
    revision: model?.revision || "",
    category: model?.category || "",
  });
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/api/model-registry/${model.name}`, {
          description: f.description,
          category: f.category || null,
          ...(custom ? { model_id: f.model_id.trim(), revision: f.revision || null } : {}),
        });
      } else {
        await api.post("/api/model-registry", {
          model_id: f.model_id.trim(),
          name: f.name.trim() || undefined,
          description: f.description || undefined,
          revision: f.revision || undefined,
        });
      }
      toast.success(editing ? "Model saved" : "Model registered");
      onClose(true);
    } catch {
      /* api wrapper toasts */
    } finally {
      setBusy(false);
    }
  };

  const canSave = editing || f.model_id.trim();

  return (
    <Dialog open onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Edit ${model.name}` : "Register model"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <TextField size="small" label="HuggingFace model URL or id"
            placeholder="https://huggingface.co/facebook/bart-large-mnli"
            value={f.model_id} onChange={up("model_id")} disabled={editing && !custom}
            helperText={editing ? undefined : "Paste the model page URL or an org/repo id"} />
          {!editing && (
            <TextField size="small" label="Name (optional slug)" placeholder="auto from repo id"
              value={f.name} onChange={up("name")}
              helperText="Clients reference it as mirror://<name>; blank = derived from the repo" />
          )}
          <Stack direction="row" spacing={1.5}>
            <TextField size="small" label="Category" select value={f.category}
              onChange={up("category")} sx={{ flex: 1 }}>
              {CATEGORIES.map((c) => <MenuItem key={c || "auto"} value={c}>{c || "auto-detect"}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Revision (optional)" placeholder="main / a commit"
              value={f.revision} onChange={up("revision")} disabled={editing && !custom} sx={{ flex: 1 }} />
          </Stack>
          <TextField size="small" label="Description" value={f.description} onChange={up("description")} />
          <Typography variant="caption" color="text.secondary">
            Registering only records the model — hit Mirror to download it from HuggingFace into S3.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} color="inherit">Cancel</Button>
        <Button onClick={save} disabled={busy || !canSave} variant="contained">
          {editing ? "Save" : "Register"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
